import React, { useEffect, useState, useCallback } from 'react';
import { sb, Company } from '../services/supabase';
import { PageHeader, SearchBar, Card, Table, Td, EmptyRow, Btn, Badge, Modal, FormField, Input, Select, useToast, CompanyInit } from '../components/ui';

export default function Companies() {
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState<'add' | 'edit' | 'features' | null>(null);
  const [editing, setEditing] = useState<Company | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', billing_email: '', industry: '', city: '', plan: 'trial' });
  const [features, setFeatures] = useState({ feature_attendance: true, feature_leave: false, feature_payroll: false });

  const load = useCallback(async () => {
    const { data } = await sb.from('companies').select('*').order('created_at', { ascending: false });
    setCompanies(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = companies.filter(c => c.name.toLowerCase().includes(query.toLowerCase()) || c.slug?.toLowerCase().includes(query.toLowerCase()));

  const openAdd = () => {
    setForm({ name: '', slug: '', billing_email: '', industry: '', city: '', plan: 'trial' });
    setEditing(null); setModal('add');
  };

  const openEdit = (c: Company) => {
    setForm({ name: c.name, slug: c.slug || '', billing_email: c.billing_email || '', industry: c.industry || '', city: c.city || '', plan: c.plan || 'trial' });
    setEditing(c); setModal('edit');
  };

  const openFeatures = (c: Company) => {
    setFeatures({ feature_attendance: c.feature_attendance !== false, feature_leave: !!c.feature_leave, feature_payroll: !!c.feature_payroll });
    setEditing(c); setModal('features');
  };

  const save = async () => {
    if (!form.name) { toast('Name required', 'error'); return; }
    setSaving(true);
    const slug = form.slug || form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (editing) {
      const { error } = await sb.from('companies').update({ ...form, slug }).eq('id', editing.id);
      if (error) { toast(error.message, 'error'); setSaving(false); return; }
      toast('Company updated!', 'success');
    } else {
      const { error } = await sb.from('companies').insert({ ...form, slug, is_active: true });
      if (error) { toast(error.message, 'error'); setSaving(false); return; }
      toast('Company created!', 'success');
    }
    setSaving(false); setModal(null); load();
  };

  const saveFeatures = async () => {
    if (!editing) return;
    setSaving(true);
    const { error } = await sb.from('companies').update(features).eq('id', editing.id);
    setSaving(false);
    if (error) { toast(error.message, 'error'); return; }
    toast('Features updated!', 'success'); setModal(null); load();
  };

  const toggleActive = async (c: Company) => {
    await sb.from('companies').update({ is_active: !c.is_active }).eq('id', c.id);
    toast(c.is_active ? 'Company deactivated' : 'Company activated', 'success'); load();
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Companies" subtitle={`${companies.length} total`} action={<Btn onClick={openAdd}>+ Add Company</Btn>} />
      <SearchBar placeholder="Search companies..." onSearch={setQuery} />
      <Card>
        <Table headers={['Company', 'Plan', 'Features', 'Status', 'Created', 'Actions']}>
          {filtered.length === 0 ? <EmptyRow cols={6} /> : filtered.map(c => (
            <tr key={c.id}>
              <Td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CompanyInit name={c.name} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{c.slug}</div>
                    {c.city && <div style={{ fontSize: 11, color: 'var(--muted)' }}>📍 {c.city}</div>}
                  </div>
                </div>
              </Td>
              <Td><Badge type={c.plan || 'trial'}>{c.plan || 'trial'}</Badge></Td>
              <Td>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: c.feature_attendance !== false ? '#00C9A720' : 'var(--surface2)', color: c.feature_attendance !== false ? 'var(--accent)' : 'var(--muted)' }}>Attend</span>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: c.feature_leave ? '#845EC220' : 'var(--surface2)', color: c.feature_leave ? '#845EC2' : 'var(--muted)' }}>Leave</span>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: c.feature_payroll ? '#F9A82520' : 'var(--surface2)', color: c.feature_payroll ? 'var(--warn)' : 'var(--muted)' }}>Payroll</span>
                </div>
              </Td>
              <Td><Badge type={c.is_active ? 'green' : 'red'}>{c.is_active ? 'active' : 'inactive'}</Badge></Td>
              <Td mono>{new Date(c.created_at).toLocaleDateString('en-IN')}</Td>
              <Td>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Btn variant="outline" size="sm" onClick={() => openEdit(c)}>Edit</Btn>
                  <Btn variant="warn" size="sm" onClick={() => openFeatures(c)}>⚙ Features</Btn>
                  <Btn variant="danger" size="sm" onClick={() => toggleActive(c)}>{c.is_active ? 'Disable' : 'Enable'}</Btn>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'Add Company' : 'Edit Company'} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><FormField label="Company Name"><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Ramsun Autos" /></FormField></div>
            <FormField label="Slug (URL)"><Input value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value}))} placeholder="ramsun-autos" /></FormField>
            <FormField label="Plan">
              <Select value={form.plan} onChange={e => setForm(f => ({...f, plan: e.target.value}))}>
                <option value="trial">Trial</option><option value="starter">Starter (₹40/emp)</option>
                <option value="growth">Growth (₹30/emp)</option><option value="enterprise">Enterprise (₹20/emp)</option>
              </Select>
            </FormField>
            <FormField label="Billing Email"><Input type="email" value={form.billing_email} onChange={e => setForm(f => ({...f, billing_email: e.target.value}))} /></FormField>
            <FormField label="Industry"><Input value={form.industry} onChange={e => setForm(f => ({...f, industry: e.target.value}))} placeholder="Automotive" /></FormField>
            <div style={{ gridColumn: '1/-1' }}><FormField label="City"><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} placeholder="Trichy" /></FormField></div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : modal === 'add' ? 'Create Company' : 'Save'}</Btn>
          </div>
        </Modal>
      )}

      {modal === 'features' && editing && (
        <Modal title={`Feature Flags — ${editing.name}`} onClose={() => setModal(null)}>
          <div style={{ background: '#00C9A710', border: '1px solid #00C9A730', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--accent)' }}>
            ⚙️ Enable or disable modules for this company based on their subscription plan.
          </div>
          {[
            ['feature_attendance', '✅ Attendance Module', 'Core check-in/out with face recognition and geofencing', 'All plans'],
            ['feature_leave', '🌴 Leave Management', 'Leave requests, types, policy, and approval workflow', 'Growth+'],
            ['feature_payroll', '💰 Payroll Module', 'Monthly payroll calculation with salary structures', 'Enterprise'],
          ].map(([key, label, desc, plan]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{desc}</div>
                <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2, fontFamily: 'var(--mono)' }}>{plan}</div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={(features as any)[key]} onChange={e => setFeatures(f => ({...f, [key]: e.target.checked}))} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                <span style={{ fontSize: 13, color: (features as any)[key] ? 'var(--accent)' : 'var(--muted)', fontWeight: 600 }}>{(features as any)[key] ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={saveFeatures} disabled={saving}>{saving ? 'Saving...' : 'Save Features'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
